import { useState } from 'react'
import { Shield, Bot, Server, Link2, MousePointerClick, Gauge, Smartphone, UserX } from 'lucide-react'
import { useTrackerProtection } from '@/api/hooks/useTracker'

// Rótulos amigáveis + ícone por camada (a chave técnica vem do backend)
const LAYERS: Record<string, { label: string; icon: typeof Shield; js?: boolean; hint: string }> = {
  missing_token_social: { label: 'Sem origem social / token', icon: UserX, hint: 'Não veio do Facebook/Instagram e sem token válido (ex: acesso direto, revisor).' },
  user_agent:           { label: 'Robôs (User-Agent)', icon: Bot, hint: 'Identificou-se como robô conhecido (Googlebot, AdSense, crawlers).' },
  datacenter_ip:        { label: 'IP de servidor/datacenter', icon: Server, hint: 'Acesso de dentro do Google / nuvem, não de um celular real.' },
  js_automation:        { label: 'Automação (Selenium/Puppeteer)', icon: Bot, js: true, hint: 'Marcas de ferramenta de automação detectadas no navegador.' },
  js_webdriver:         { label: 'Automação (webdriver)', icon: Bot, js: true, hint: 'Navegador controlado por robô (flag webdriver).' },
  js_non_mobile:        { label: 'Desktop (não-mobile)', icon: Smartphone, js: true, hint: 'Não é celular — o overlay é só pra tráfego mobile.' },
  csrf_invalid:         { label: 'Token de sessão inválido', icon: Link2, hint: 'Chave de uso único expirada ou já usada.' },
  no_csrf_token:        { label: 'Acesso direto (sem token)', icon: Link2, hint: 'Chamou o endereço do overlay sem passar pelo nosso script.' },
  rate_limit:           { label: 'Varredura (rate-limit)', icon: Gauge, hint: 'Abriu o overlay vezes demais na mesma hora.' },
  device_limit:         { label: 'Limite por aparelho', icon: Smartphone, hint: 'Já viu o overlay o máximo de vezes permitido no período.' },
}

const COLORS = ['#ef4444', '#f59e0b', '#eab308', '#a855f7', '#3b82f6', '#06b6d4', '#64748b', '#94a3b8', '#cbd5e1']

function fmt(n: number) {
  return n.toLocaleString('pt-BR')
}

export default function ProtectionTab({ siteId }: { siteId: string }) {
  const [days, setDays] = useState(1)
  const { data, isLoading } = useTrackerProtection(siteId, days)

  const served = data?.served ?? 0
  const blocked = data?.blocked ?? 0
  const total = served + blocked
  const pctBlocked = total > 0 ? Math.round((blocked / total) * 100) : 0
  const pctServed = 100 - pctBlocked

  // Ordena as camadas por contagem desc
  const layers = Object.entries(data?.by_layer ?? {}).sort((a, b) => b[1] - a[1])
  const maxLayer = layers.length ? layers[0][1] : 0

  return (
    <div className="space-y-4">
      {/* Cabeçalho + seletor de período */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Shield className="h-4 w-4 text-amber-500" /> Proteção do overlay
        </h3>
        <div className="flex gap-1 rounded-lg border border-border p-0.5">
          {[{ d: 1, l: 'Hoje' }, { d: 7, l: '7 dias' }].map((o) => (
            <button key={o.d} type="button" onClick={() => setDays(o.d)}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${days === o.d ? 'bg-amber-500 text-white' : 'text-muted-foreground hover:bg-muted'}`}>
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Carregando…</div>
      ) : (
        <>
          {/* Funil servido vs bloqueado */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="text-2xl font-bold text-emerald-500">{fmt(served)}</div>
                <div className="text-xs text-muted-foreground">overlays servidos (lead real passou)</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-red-500">{fmt(blocked)}</div>
                <div className="text-xs text-muted-foreground">bloqueados (robô/crawler/auditor)</div>
              </div>
            </div>
            {/* Barra do funil */}
            <div className="h-3 rounded-full overflow-hidden bg-muted flex">
              <div className="bg-emerald-500 h-full" style={{ width: `${pctServed}%` }} />
              <div className="bg-red-500 h-full" style={{ width: `${pctBlocked}%` }} />
            </div>
            <div className="text-[11px] text-muted-foreground text-center">
              <span className="text-emerald-500 font-medium">{pctServed}% passou</span> ·{' '}
              <span className="text-red-500 font-medium">{pctBlocked}% filtrado</span>
              {total === 0 && ' — sem dados no período ainda'}
            </div>
          </div>

          {/* Quebra por camada */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs font-semibold text-muted-foreground mb-3">Quem foi barrado (por camada)</div>
            {layers.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Nenhum bloqueio no período.</div>
            ) : (
              <div className="space-y-2.5">
                {layers.map(([key, count], i) => {
                  const meta = LAYERS[key] ?? { label: key, icon: Shield, hint: '' }
                  const Icon = meta.icon
                  const pct = maxLayer > 0 ? Math.round((count / maxLayer) * 100) : 0
                  return (
                    <div key={key} title={meta.hint}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          {meta.label}
                          {meta.js && <span className="text-[9px] px-1 rounded bg-blue-500/15 text-blue-500">JS</span>}
                        </span>
                        <span className="font-semibold tabular-nums">{fmt(count)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border">
              <span className="text-[9px] px-1 rounded bg-blue-500/15 text-blue-500">JS</span> = detectado no navegador do visitante (via beacon). As demais são barradas no servidor.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
